import { Component, OnInit, Input } from '@angular/core';
import { Business} from '../models/business.model';
import { hasTag, loadTags } from '../../util/tag-utils';
import { IConditionalSection } from '../../shared/models/condition-section.model';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { MatDialog, MatDialogConfig, MatDialogRef, MatSnackBar } from '@angular/material';
import { EditTagsComponent } from '../../shared/edit-tags/edit-tags.component';
import { EditDescComponent } from '../edit-desc/edit-desc.component';
import { EditClubDetailsComponent } from '../edit-club-details/edit-club-details.component';
import { BusinessManagerService } from '../../shared/user/business-manager.service';


enum DialogType {
  features,
  description,
  details
}

@Component({
  selector: 'app-flying-club',
  templateUrl: './flying-club.component.html',
  styleUrls: ['./flying-club.component.scss']
})

export class FlyingClubComponent implements OnInit, IConditionalSection {
  @Input() business: Business;
  facilitiesTags: String[] = [];
  typesOfFlyingTags: String[] = [];
  hasTag = hasTag;
  editModeSet = false;
  isAmbassador = false;

  constructor(private _toggleService: EditToggleService,
              private _editFeaturesDialog: MatDialog,
              private _editDescDialog: MatDialog,
              public snackBar: MatSnackBar,
              private _businessManagerService: BusinessManagerService) { }

  async ngOnInit() {
    this._loadAllTags();
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
    if (hasTag(this.business.tags, 'Flying Club') && this.business.flyingClub) {
      this.isAmbassador = await this._businessManagerService.isAmbassador();
    }
  }

  get hasContent(): boolean {
    return this.business && !!this.business.description || this.hasClubFeatureData || this.hasClubDetailData;
  }

  get hasClubFeatureData(): boolean {
    return (this.facilitiesTags.length > 0 || this.typesOfFlyingTags.length > 0);
  }

  get hasClubDetailData(): boolean {
    if (!this.business) {
      return false;
    }
    if (hasTag(this.business.tags, 'Flying Club') && this.business.flyingClub) {
      if (this.business.flyingClub.clubAircraft || this.business.flyingClub.clubMembers) {
        return true;
      }
    }
    return (hasTag(this.business.tags, 'Flying Club::AOPA::AOPA Network')
      || hasTag(this.business.tags, 'Flying Club::Membership::Open')
      || hasTag(this.business.tags, 'Flying Club::Equity Club'));
  }

  _loadAllTags() {
    this.facilitiesTags = loadTags(this.business.tags, 'Flying Club::Benefits::', '', '');
    this.typesOfFlyingTags = loadTags(this.business.tags, 'Flying Club::Flying::', '', '');
  }

  _openModal(component: number, data: any = {}): MatDialogRef<any> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = data;

    dialogConfig.width = '100%';

    switch (component) {
      case DialogType.features: {
        return this._editFeaturesDialog.open(EditTagsComponent, dialogConfig);
      }
      case DialogType.description: {
        return this._editDescDialog.open(EditDescComponent, dialogConfig);
      }
      case DialogType.details: {
        const MODAL_WIDTH = Math.floor(100 + ((550 - window.innerWidth) / (10 * (window.innerWidth / 1000))));
        dialogConfig.width = MODAL_WIDTH <= 100 ? MODAL_WIDTH + '%'  : '100%';
        return this._editDescDialog.open(EditClubDetailsComponent, dialogConfig);
      }
    }
  }

  /**
   * listens for dialog close event. Opens snackbar if necessary
   * @param {MatDialogRef<any>} dialogRef
   * @private
   */
  _handleModalClose ( dialogRef: MatDialogRef<any>) {
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';

    dialogRef.afterClosed().subscribe((success) => {
      if (success === null) { return; }
      if (success) {
        this.snackBar.open('Club Description', 'Updated', {
          duration: 3000
        });
      } else {
        this.snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  /**
   * Opens modal for editing business overview section, displays snackbar if serverAPI was successfully updated.
   */
  async openEditFeaturesDialog(group: String[], title: string) {
    const DATA = {
      business: this.business,
      tagsGroup: group,
      title: title
    };
    const DIALOG_REF = this._openModal(DialogType.features, DATA);
    const SUCCESS = await DIALOG_REF.afterClosed().toPromise();
    if (SUCCESS) {
      this._loadAllTags();
    }
  }

  openEditDescDialog() {
    const DATA = {business: this.business};
    const DIALOG_REF = this._openModal(DialogType.description, DATA);
    this._handleModalClose(DIALOG_REF);
  }

  openEditDetailsDialog() {
    const DATA = {business: this.business};
    const DIALOG_REF = this._openModal(DialogType.details, DATA);
    this._handleModalClose(DIALOG_REF);
  }
}
